import {
  FEATURED_ITEM_DESCRIPTION_MAX_LENGTH,
  FEATURED_ITEM_NAME_MAX_LENGTH,
  FEATURED_ITEM_PRICE_MAX,
} from '@kardume/types';
import type { MerchantFeaturedItemDto, MerchantFeaturedItemRequestDto } from '@kardume/types';

/**
 * O formulário de um item em destaque, separado do componente de propósito.
 *
 * O que este arquivo guarda é a única regra do card #218 que não pode errar
 * (§2 do spec 033): **campo de preço vazio vira `price: null`, nunca `0`.**
 * Fora de um `.tsx`, essa conversão é uma função pura de três linhas com teste
 * ao lado (`featuredItemForm.test.ts`) - dentro do JSX, ela seria um `??`
 * escondido no meio de um `onSubmit` e ninguém a testaria.
 */

/**
 * O estado em memória enquanto o lojista digita.
 *
 * Texto é `string` (o `''` do campo em branco), preço é `number | undefined`
 * (o vazio do `CurrencyInput`). A tradução para o `null` do contrato acontece
 * uma vez só, em `toFeaturedItemRequest`.
 */
export interface FeaturedItemFormState {
  name: string;
  description: string;
  imageUrl: string | undefined;
  price: number | undefined;
}

export const emptyFeaturedItemForm = (): FeaturedItemFormState => ({
  name: '',
  description: '',
  imageUrl: undefined,
  price: undefined,
});

/**
 * Abre um item salvo para edição.
 *
 * `price ?? undefined` - e nunca `price ?? 0`: o item que chegou sem preço tem
 * de reabrir com o campo VAZIO, senão o simples ato de abrir e salvar de novo
 * inventaria um "R$ 0,00" que o lojista nunca digitou.
 */
export const toFeaturedItemForm = (item: MerchantFeaturedItemDto): FeaturedItemFormState => ({
  name: item.name,
  description: item.description ?? '',
  imageUrl: item.imageUrl ?? undefined,
  price: item.price ?? undefined,
});

/**
 * Monta o corpo do POST/PUT.
 *
 * Campo vazio vira `null` em todos os quatro: é o `null` que faz o core gravar
 * a coluna nula, e é a coluna nula que faz o site e o app **não desenharem a
 * linha do preço**. `''` seria uma descrição vazia gravada, e `0` seria um
 * preço afirmado.
 */
export const toFeaturedItemRequest = (
  form: FeaturedItemFormState
): MerchantFeaturedItemRequestDto => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  imageUrl: form.imageUrl?.trim() || null,
  // `?? null`, jamais `?? 0`. Ver o §2 do spec 033: item sem preço aparece sem
  // preço, e um zero na tela é lido como fato quando o fato é "não se sabe".
  price: form.price ?? null,
});

export type FeaturedItemFormErrors = Partial<Record<keyof FeaturedItemFormState, string>>;

/**
 * Mesmas réguas do core, avisadas aqui para o lojista não descobrir o limite
 * por um 400 depois de escrever a descrição inteira. Quem valida de verdade
 * continua sendo o core.
 */
export function validateFeaturedItem(form: FeaturedItemFormState): FeaturedItemFormErrors {
  const errors: FeaturedItemFormErrors = {};

  const name = form.name.trim();
  if (!name) errors.name = 'Informe o nome do item.';
  else if (name.length > FEATURED_ITEM_NAME_MAX_LENGTH) {
    errors.name = `Use no máximo ${FEATURED_ITEM_NAME_MAX_LENGTH} caracteres.`;
  }

  if (form.description.trim().length > FEATURED_ITEM_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Use no máximo ${FEATURED_ITEM_DESCRIPTION_MAX_LENGTH} caracteres.`;
  }

  /*
    Preço: vazio é válido - é o ponto do card. O que não é válido é um preço
    declarado fora da faixa que o `ck_merchant_featured_item_price` aceita.

    O zero não chega aqui (o `allowEmpty` do `CurrencyInput` o traduz para
    vazio), mas a guarda fica: ela é a régua do banco escrita por extenso, e o
    dia em que alguém montar este estado sem passar pelo campo, o `> 0` do
    check não vai ser descoberto por um 400 sem explicação.
  */
  if (typeof form.price === 'number') {
    if (form.price <= 0) {
      errors.price = 'Deixe em branco se o item não tem preço fixo - R$ 0,00 não é um preço.';
    } else if (form.price > FEATURED_ITEM_PRICE_MAX) {
      errors.price = `O preço deve ser de no máximo ${FEATURED_ITEM_PRICE_MAX.toLocaleString('pt-BR')}.`;
    }
  }

  return errors;
}

export const hasFeaturedItemErrors = (errors: FeaturedItemFormErrors): boolean =>
  Object.values(errors).some(Boolean);
